import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Send,
  Loader2,
  User,
  Bot,
  Edit2,
  Trash2,
  Pin,
  PinOff,
  MoreVertical,
  AlertCircle,
} from "lucide-react";
import { useAuth } from "../App";
import { useParams } from "react-router-dom";
import { io, Socket } from "socket.io-client";
import {
  getProjectDiscussion,
  getProjectDiscussionMessages,
  sendProjectMessage,
  editProjectMessage,
  deleteProjectMessage,
  pinProjectMessage,
  getDiscussionSocketToken,
  DiscussionMessage,
} from "../api/projectDiscussionApi";

interface ProjectMessagesChatProps {
  projectId?: string;
  isDarkMode?: boolean;
}

const ProjectMessagesChat: React.FC<ProjectMessagesChatProps> = ({
  projectId,
  isDarkMode = false,
}) => {
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const { user } = useAuth();
  const { projectId: paramProjectId } = useParams();
  const activeProjectId = projectId || paramProjectId;

  const [messages, setMessages] = useState<DiscussionMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingMessages, setIsFetchingMessages] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [projectName, setProjectName] = useState("");
  const [isSocketConnected, setIsSocketConnected] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastMessageIdRef = useRef<string | null>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Polling mechanism for fallback (checks every 3 seconds when socket is disconnected)
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      if (!activeProjectId || isSocketConnected) {
        return;
      }

      try {
        const response = await getProjectDiscussion(activeProjectId, 50);
        const newMessages = response.data.messages;

        if (newMessages.length > 0) {
          const latestMessageId = newMessages[newMessages.length - 1].id;
          if (lastMessageIdRef.current !== latestMessageId) {
            setMessages(newMessages);
            lastMessageIdRef.current = latestMessageId;
          }
        }
      } catch (err) {
        console.error("Failed to poll messages:", err);
      }
    }, 3000);
  }, [activeProjectId, isSocketConnected]);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpenId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Initialize Socket.IO connection
  const initializeSocket = useCallback(async () => {
    if (!activeProjectId) return;

    try {
      const { data } = await getDiscussionSocketToken(activeProjectId);
      const { token, socketUrl } = data;

      const socket = io(socketUrl, {
        path: "/socket.io",
        auth: { token },
        query: { projectId: activeProjectId },
        transports: ["websocket", "polling"],
      });

      socket.on("connect", () => {
        setIsSocketConnected(true);
        setError(null);
        stopPolling();
      });

      socket.on("disconnect", (reason) => {
        setIsSocketConnected(false);
        startPolling();
      });

      socket.on("connect_error", (err) => {
        setIsSocketConnected(false);
        startPolling();
        setError("Real-time connection failed. Using polling for updates.");
        setTimeout(() => setError(null), 5000);
      });

      socket.on("room_joined", () => {});

      socket.on("new_message", (data: { message: DiscussionMessage }) => {
        const { message } = data;
        if (!message || !message.id) return;

        setMessages((prev) => {
          const exists = prev.some(
            (msg) => msg?.id === message.id && !msg?.id?.startsWith("temp-"),
          );
          if (exists) return prev;

          if (message.author_id === user?.id) {
            const tempIndex = prev.findIndex((msg) =>
              msg?.id?.startsWith("temp-"),
            );
            if (tempIndex !== -1) {
              const newMessages = [...prev];
              newMessages[tempIndex] = message;
              return newMessages;
            }
          }

          return [...prev, message];
        });
      });

      socket.on("message_edited", (data: { message: DiscussionMessage }) => {
        const { message } = data;
        setMessages((prev) =>
          prev.map((msg) => (msg.id === message.id ? message : msg)),
        );
      });

      socket.on("message_deleted", (data: { messageId: string }) => {
        const { messageId } = data;
        setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
      });

      socket.on("message_pinned", (data: { message: DiscussionMessage }) => {
        const { message } = data;
        setMessages((prev) =>
          prev.map((msg) => (msg.id === message.id ? message : msg)),
        );
      });

      socketRef.current = socket;
    } catch (err) {
      setIsSocketConnected(false);
      startPolling();
      setError("Real-time connection failed. Using polling for updates.");
      setTimeout(() => setError(null), 5000);
    }
  }, [activeProjectId, user?.id, startPolling, stopPolling]);

  // Load initial messages
  useEffect(() => {
    if (!activeProjectId) return;

    const loadMessages = async () => {
      setIsFetchingMessages(true);
      setError(null);
      setIsInitialLoading(true);

      try {
        const response = await getProjectDiscussion(activeProjectId, 50);
        setMessages(response.data.messages);
        setProjectName(response.data.project.name);
        setHasMore(response.data.messages.length >= 50);

        // Track last message ID for polling
        if (response.data.messages.length > 0) {
          lastMessageIdRef.current =
            response.data.messages[response.data.messages.length - 1].id;
        }

        // Clear any previous errors on successful load
        setError(null);
      } catch (err: any) {
        console.error("Failed to load messages:", err);
        //  setError(err.response?.data?.message || "Failed to load messages");
      } finally {
        setIsFetchingMessages(false);
        setIsInitialLoading(false);
      }
    };

    loadMessages();
    initializeSocket();

    const pollingTimeout = setTimeout(() => {
      if (!isSocketConnected) {
        startPolling();
      }
    }, 3000);

    return () => {
      clearTimeout(pollingTimeout);
      stopPolling();
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [
    activeProjectId,
    initializeSocket,
    startPolling,
    stopPolling,
    isSocketConnected,
  ]);

  // Load more messages (infinite scroll)
  const loadMoreMessages = async () => {
    if (!activeProjectId || !hasMore || isFetchingMessages) return;

    setIsFetchingMessages(true);
    try {
      const oldestMessage = messages[0];
      const response = await getProjectDiscussionMessages(
        activeProjectId,
        oldestMessage.created_at,
        50,
      );

      setMessages((prev) => [...response.data.messages, ...prev]);
      setHasMore(response.data.hasMore);
    } catch (err) {
      console.error("Failed to load more messages:", err);
    } finally {
      setIsFetchingMessages(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || isLoading || !activeProjectId) return;

    const messageContent = newMessage.trim();
    setNewMessage("");
    setIsLoading(true);
    setError(null);

    const tempMessage: DiscussionMessage = {
      id: `temp-${Date.now()}`,
      project_id: activeProjectId,
      author_id: user?.id || "",
      content: messageContent,
      is_pinned: false,
      is_deleted: false,
      is_edited: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      author: {
        id: user?.id || "",
        name: user?.name || "You",
        email: user?.email || "",
        role: user?.role || "student",
      },
    };

    setMessages((prev) => [...prev, tempMessage]);

    try {
      await sendProjectMessage(activeProjectId, messageContent);

      const response = await getProjectDiscussion(activeProjectId, 50);
      setMessages(response.data.messages);
      setHasMore(response.data.messages.length >= 50);

      if (response.data.messages.length > 0) {
        lastMessageIdRef.current =
          response.data.messages[response.data.messages.length - 1].id;
      }
    } catch (err: any) {
      console.error("Failed to send message:", err);
      setError(err.response?.data?.message || "Failed to send message");
      setMessages((prev) => prev.filter((msg) => msg?.id !== tempMessage.id));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditMessage = async (messageId: string) => {
    if (!editContent.trim() || !activeProjectId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await editProjectMessage(
        activeProjectId,
        messageId,
        editContent.trim(),
      );
      setMessages((prev) =>
        prev.map((msg) => (msg.id === messageId ? response.data.message : msg)),
      );
      setEditingMessageId(null);
      setEditContent("");
    } catch (err: any) {
      console.error("Failed to edit message:", err);
      setError(err.response?.data?.message || "Failed to edit message");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (!activeProjectId) return;
    if (!confirm("Are you sure you want to delete this message?")) return;

    setIsLoading(true);
    setError(null);

    try {
      await deleteProjectMessage(activeProjectId, messageId);
      setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
    } catch (err: any) {
      console.error("Failed to delete message:", err);
      setError(err.response?.data?.message || "Failed to delete message");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePinMessage = async (messageId: string, isPinned: boolean) => {
    if (!activeProjectId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await pinProjectMessage(
        activeProjectId,
        messageId,
        !isPinned,
      );
      setMessages((prev) =>
        prev.map((msg) => (msg.id === messageId ? response.data.message : msg)),
      );
    } catch (err: any) {
      console.error("Failed to pin/unpin message:", err);
      setError(err.response?.data?.message || "Failed to pin/unpin message");
    } finally {
      setIsLoading(false);
    }
  };

  const canEditMessage = (message: DiscussionMessage) => {
    if (!message || !message.author_id || message.author_id !== user?.id)
      return false;
    if (message.id?.startsWith("temp-")) return false; // Can't edit temp messages
    const messageTime = new Date(message.created_at).getTime();
    const now = Date.now();
    const thirtyMinutes = 30 * 60 * 1000;
    return now - messageTime < thirtyMinutes;
  };

  const canDeleteMessage = (message: DiscussionMessage) => {
    if (!message || !message.author_id) return false;
    if (message.id?.startsWith("temp-")) return false; // Can't delete temp messages
    return (
      message.author_id === user?.id ||
      user?.role === "instructor" ||
      user?.role === "admin"
    );
  };

  const canPinMessage = () => {
    return user?.role === "instructor" || user?.role === "admin";
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Sort messages: pinned first, then by date
  const sortedMessages = messages
    .filter((msg): msg is DiscussionMessage => {
      // Filter out any null/undefined messages
      return msg != null && msg.id != null && msg.author != null;
    })
    .sort((a, b) => {
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;
      return (
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
    });

  return (
    <div className="flex flex-col h-full min-h-0 bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-[#30363d] rounded-lg">
      {/* Header */}
      <div className="flex-shrink-0 border-b dark:border-[#30363d] bg-gray-50 dark:bg-[#161b22] px-4 py-2 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-[#f0f6fc]">
              Project Discussion
            </h2>
            <p className="text-xs text-gray-500 dark:text-[#8b949e] mt-1">
              {" "}
              {projectName || "Chat with your team about this project"}{" "}
            </p>
          </div>
        </div>
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div className="flex-shrink-0 px-4 py-2 border-b dark:border-[#30363d]">
          <button
            onClick={loadMoreMessages}
            disabled={isFetchingMessages}
            className="w-full py-2 text-sm text-blue-600 dark:text-[#f0883e] hover:text-blue-800 dark:hover:text-[#e0772d] disabled:text-gray-400 dark:disabled:text-gray-600"
          >
            {isFetchingMessages ? "Loading..." : "Load older messages"}
          </button>
        </div>
      )}

      {/* Messages List - Only this area scrolls */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-4 py-4 dark:bg-[#0d1117]"
        style={{ minHeight: 0, maxHeight: "calc(100vh - 300px)" }}
      >
        <div className="flex flex-col">
          {isFetchingMessages && messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-gray-600">
              <Loader2 size={48} className="mb-2 animate-spin" />
              <p className="text-sm">Loading messages...</p>
            </div>
          ) : sortedMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-gray-600">
              <Bot size={48} className="mb-2" />
              <p className="text-sm">No messages yet</p>
              <p className="text-xs">Start a conversation with your team</p>
            </div>
          ) : (
            sortedMessages.map((message) => {
              const isCurrentUser = message.author_id === user?.id;
              const isEditing = editingMessageId === message.id;
              return (
                <div
                  key={message.id}
                  className={`flex items-start space-x-3 mb-3 ${
                    isCurrentUser ? "flex-row-reverse space-x-reverse" : ""
                  } ${message.is_pinned ? "bg-yellow-50 dark:bg-yellow-900/10 -mx-4 px-4 py-2 rounded" : ""}`}
                >
                  {/* Avatar */}
                  <div
                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white ${
                      isCurrentUser ? "bg-blue-500 dark:bg-[#f0883e]" : "bg-gray-500 dark:bg-[#30363d]"
                    }`}
                  >
                    {isCurrentUser ? (
                      <User size={16} />
                    ) : (
                      message.author.name.charAt(0).toUpperCase()
                    )}
                  </div>
                  {/* Message Content */}
                  <div
                    className={`flex-1 ${
                      isCurrentUser ? "items-end" : "items-start"
                    } flex flex-col`}
                  >
                    <div className="flex items-baseline space-x-2 mb-1">
                      {message.is_pinned && (
                        <Pin size={12} className="text-yellow-600 dark:text-yellow-500" />
                      )}
                      <span
                        className={`text-xs font-medium ${
                          isCurrentUser ? "text-blue-600 dark:text-[#f0883e]" : "text-gray-700 dark:text-[#c9d1d9]"
                        }`}
                      >
                        {message.author.name}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-[#8b949e]">
                        {new Date(message.created_at).toLocaleString([], {
                          year: "numeric",
                          month: "short",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </span>
                      {message.is_edited && (
                        <span className="text-xs text-gray-400 dark:text-[#8b949e] italic">
                          (edited)
                        </span>
                      )}
                    </div>
                    {isEditing ? (
                      <div className="w-full max-w-xs">
                        <textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-[#30363d] rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-[#f0883e] text-sm bg-white dark:bg-[#161b22] dark:text-[#f0f6fc]"
                          rows={3}
                          autoFocus
                        />
                        <div className="flex space-x-2 mt-2">
                          <button
                            onClick={() => handleEditMessage(message.id)}
                            disabled={isLoading}
                            className="px-3 py-1 bg-blue-600 dark:bg-[#f0883e] text-white text-xs rounded hover:bg-blue-700 dark:hover:bg-[#e0772d] disabled:bg-gray-300"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditingMessageId(null);
                              setEditContent("");
                            }}
                            className="px-3 py-1 bg-gray-200 dark:bg-[#30363d] text-gray-700 dark:text-[#c9d1d9] text-xs rounded hover:bg-gray-300 dark:hover:bg-[#21262d]"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start space-x-2">
                        <div
                          className={`px-4 py-2 rounded-lg max-w-xs ${
                            isCurrentUser
                              ? "bg-blue-500 dark:bg-[#f0883e] text-white"
                              : "bg-gray-100 dark:bg-[#161b22] text-gray-800 dark:text-[#c9d1d9]"
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap break-words">
                            {message.content}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="flex-shrink-0 px-4 py-3 border-t dark:border-[#30363d] bg-blue-50 dark:bg-[#161b22] rounded-b-lg">
        <div className="flex items-center space-x-2">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-[#30363d] rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-[#f0883e] focus:border-transparent text-sm bg-white dark:bg-[#0d1117] dark:text-[#f0f6fc]"
            rows={1}
            disabled={isLoading}
          />
          <button
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || isLoading}
            className="flex-shrink-0 px-3 py-1.5 bg-blue-600 dark:bg-[#f0883e] text-white rounded-lg hover:bg-blue-700 dark:hover:bg-[#e0772d] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            {isLoading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Send size={18} />
            )}
          </button>
        </div>
        <p className="text-xs text-gray-400 dark:text-[#8b949e] mt-1">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
};

export default ProjectMessagesChat;
